import React, { useEffect, useState } from "react";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import ActionRequiredChip from "@app/analytics/components/ActionRequiredChip";
import ActiveChip from "@app/analytics/components/ActiveChip";
import IntegrationProviderIcon from "@app/analytics/components/IntegrationProviderIcon";
import { IntegrationIcon } from "@app/common/assets/icons";
import type { Integration } from "@app/common/types";

import Button from "@rui/Button";
import { Link } from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipWrapper from "@rui/TooltipWrapper";

const ManageButton = styled(Button)(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  fontSize: "0.75rem",
  height: "fit-content",

  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: "12px",
    },
  },
}));

const SwitchLoadingOverlay = styled(Column)({
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  opacity: "50%",
});

const IntegrationModuleRow = ({
  alert,
  integration,
  isConfirming,
  isLoading,
  onToggleEnabled,
}: {
  alert?: React.ReactNode;
  integration: Integration;
  isConfirming: boolean;
  isLoading: boolean;
  onToggleEnabled: (int: Integration, enable: boolean) => void;
}) => {
  const [pendingAction, setPendingAction] = useState<
    "enable" | "disable" | null
  >(null);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabling = event.target.checked;
    setPendingAction(enabling ? "enable" : "disable");
    onToggleEnabled(integration, enabling);
  };

  useEffect(() => {
    setPendingAction(null);
  }, [integration.isConnected, isConfirming]);

  const isActionPending = pendingAction != null;
  const integrationIsLoading = isLoading || isActionPending;

  return (
    <Column spacing={0.5}>
      <Row variant="apart" spacing={1.5}>
        <Row spacing={1.5} variant="left">
          <IntegrationProviderIcon src={integration.icon} />
          <Column>
            <Row variant="left" spacing={1}>
              <Typography variant="bodyPrimaryMed">
                {integration.displayName}
              </Typography>
              {integration.reauthRequired ? (
                <Link to={integration.route}>
                  <ActionRequiredChip severity="error" />
                </Link>
              ) : integration.isConnected ? (
                <ActiveChip />
              ) : null}
            </Row>
            {integration.isConnected && integration.providerEmail && (
              <Typography variant="labelReg" color="shades.neutral.600">
                {integration.providerEmail} is connected
              </Typography>
            )}
          </Column>
        </Row>
        <Row variant="right">
          {(integration.isConnected || integration.category === "workflow") && (
            <ManageButton
              component={Link}
              to={integration.route}
              variant="text"
              startIcon={<IntegrationIcon />}
            >
              Manage
            </ManageButton>
          )}
          {integration.category !== "workflow" && (
            <Column position="relative">
              <TooltipWrapper
                show={integration.isDisabled}
                title="Your Workspace has restricted access to this integration"
              >
                <Switch
                  onChange={handleSwitchChange}
                  checked={integration.isConnected}
                  disabled={integration.isDisabled || integrationIsLoading}
                />
              </TooltipWrapper>
              {isActionPending && (
                <SwitchLoadingOverlay variant="center">
                  <LoadSpinner size={16} delay={500} />
                </SwitchLoadingOverlay>
              )}
            </Column>
          )}
        </Row>
      </Row>
      {alert && <Row marginLeft="50px">{alert}</Row>}
    </Column>
  );
};

export default IntegrationModuleRow;
